import { AfterViewInit, Component, ElementRef, Inject, input, OnInit, Self, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { DateAdapter, ErrorStateMatcher, MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';
import { MatDatepickerInput, MatDatepickerInputEvent } from '@angular/material/datepicker';

@Component({
  selector: 'eiq-input-date',
  templateUrl: 'input-date.component.html',
  styleUrls: ['input-date.component.scss']
})
export class InputDateComponent implements OnInit, AfterViewInit, ControlValueAccessor {
  @ViewChild('dateInput') dateInput?: ElementRef<HTMLInputElement>;
  @ViewChild(MatDatepickerInput) matDatepickerInput?: MatDatepickerInput<Date>;

  disabled = input<boolean>(false);
  hint = input<string>();
  label = input<string>();
  max = input<Date | string>();
  min = input<Date | string>();
  testId = input<string>();

  control: FormControl;
  currentValue = '';
  isFormDisabled = false;
  readonly errorStateMatcher: ErrorStateMatcher = {
    isErrorState: () => !!this.control?.invalid && !!this.control?.touched
  };

  constructor(
    @Self() public ngControl: NgControl,
    private dateAdapter: DateAdapter<Date>,
    @Inject(MAT_DATE_FORMATS) private dateFormats: MatDateFormats
  ) {
    this.ngControl.valueAccessor = this;
  }

  onChange: (value: Date | null) => void = () => undefined;
  onTouched: () => void = () => undefined;

  ngOnInit(): void {
    this.control = this.ngControl.control as FormControl;
  }

  ngAfterViewInit(): void {
    this.syncDisplayedValue();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(newValue: Date | string | null): void {
    this.commitValue(this.parseDateValue(newValue));
  }

  onInput($event: Event): void {
    const dateTyped = $event.target as HTMLInputElement | null;
    if (!dateTyped) {
      return;
    }

    const previousValue = this.currentValue;
    const rawValue = dateTyped.value;
    const selectionStart = dateTyped.selectionStart ?? rawValue.length;
    const caretAtEnd = selectionStart === rawValue.length;
    const digitsBeforeCaret = rawValue.slice(0, selectionStart).replace(/\D/g, '').length;
    let maskedValue = this.maskDateValue(rawValue);

    if (!this.isPartialDateAllowed(maskedValue)) {
      maskedValue = previousValue;
    }

    this.currentValue = maskedValue;
    this.syncDisplayedValue();

    if (rawValue !== maskedValue) {
      dateTyped.value = maskedValue;
      const nextCaretPosition = caretAtEnd ? maskedValue.length : this.getCaretPosition(maskedValue, digitsBeforeCaret);
      requestAnimationFrame(() => dateTyped.setSelectionRange(nextCaretPosition, nextCaretPosition));
    }
  }

  setValue($event: MatDatepickerInputEvent<Date>): void {
    const nextValue = this.parseDateValue($event.value);

    if (!nextValue) {
      return;
    }

    this.commitValue(nextValue);
    this.clearErrors();
    this.onTouched();
    this.onChange(nextValue);
  }

  validateOnBlur($event: FocusEvent): void {
    this.onTouched();

    const inputValue = this.getInputValue($event.target);
    const currentValue = this.control.value as Date | null;

    if (!inputValue) {
      this.commitValue(null);
      this.clearErrors();

      if (currentValue) {
        this.onChange(null);
      } else {
        this.control.updateValueAndValidity({ emitEvent: false });
      }

      return;
    }

    const parsed = this.parseDateValue(inputValue);
    if (!parsed) {
      this.syncDatepickerValue(null);
      this.onChange(null);
      this.markInvalid({ date: true });
      return;
    }

    const hasChanged = !currentValue || parsed.getTime() !== new Date(currentValue).getTime();

    this.commitValue(parsed);

    if (hasChanged) {
      this.onChange(parsed);
    }

    const minDate = this.parseDateValue(this.min());
    if (minDate && parsed < minDate) {
      this.markInvalid({ min: { min: this.formatDate(minDate) } });
      return;
    }

    const maxDate = this.parseDateValue(this.max());
    if (maxDate && parsed > maxDate) {
      this.markInvalid({ max: { max: this.formatDate(maxDate) } });
      return;
    }

    this.clearErrors();
  }

  clearValue(): void {
    this.commitValue(null);
    this.clearErrors();
    this.onChange(null);
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isFormDisabled = isDisabled;
  }

  private clearErrors(): void {
    if (!this.control.errors) {
      return;
    }

    const { date: _date, min: _min, max: _max, ...remainingErrors } = this.control.errors;

    this.control.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
  }

  private markInvalid(errors: Record<string, unknown>): void {
    this.control.setErrors(errors);
    this.control.markAsTouched();
  }

  private parseDateValue(value: Date | string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return this.dateAdapter.isValid(value) ? value : null;
    }

    const parsed = this.dateAdapter.parse(value, this.dateFormats.parse.dateInput);
    return parsed && this.dateAdapter.isValid(parsed) ? parsed : null;
  }

  private formatDate(value: Date): string {
    return this.dateAdapter.format(value, this.dateFormats.display.dateInput);
  }

  private maskDateValue(value: string): string {
    if (value.includes('/')) {
      return this.maskSlashSeparatedDateValue(value);
    }

    const digits = value.replace(/\D/g, '').slice(0, 8);

    if (digits.length <= 2) {
      return digits;
    }

    if (digits.length <= 4) {
      return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }

    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  }

  private maskSlashSeparatedDateValue(value: string): string {
    const sanitizedValue = value.replace(/[^\d/]/g, '');
    const [rawDay = '', rawMonth = '', ...rawYearParts] = sanitizedValue.split('/');
    const day = rawDay.slice(0, 2);
    const monthValue = rawMonth.slice(0, 2);
    const monthOverflow = rawMonth.slice(2);
    const year = `${monthOverflow}${rawYearParts.join('')}`.slice(0, 4);
    const hasStartedYear = rawYearParts.length > 0 || monthOverflow.length > 0 || sanitizedValue.endsWith('//');
    const month = hasStartedYear && monthValue.length === 1 ? monthValue.padStart(2, '0') : monthValue;

    let maskedValue = day;

    if (sanitizedValue.includes('/')) {
      maskedValue += '/';
    }

    if (rawMonth || sanitizedValue.split('/').length > 1) {
      maskedValue += month;
    }

    if (hasStartedYear) {
      maskedValue += '/';
    }

    if (year.length > 0) {
      maskedValue += year;
    }

    return maskedValue;
  }

  private isPartialDateAllowed(value: string): boolean {
    const [, monthValue = ''] = value.split('/');

    if (monthValue.length < 2) {
      return true;
    }

    const month = Number(monthValue);
    return month >= 1 && month <= 12;
  }

  private getCaretPosition(maskedValue: string, digitsBeforeCaret: number): number {
    if (digitsBeforeCaret <= 0) {
      return 0;
    }

    let digitCount = 0;
    for (let index = 0; index < maskedValue.length; index++) {
      if (/\d/.test(maskedValue[index])) {
        digitCount++;
      }

      if (digitCount === digitsBeforeCaret) {
        return index + 1;
      }
    }

    return maskedValue.length;
  }

  private syncDisplayedValue(): void {
    if (this.dateInput?.nativeElement) {
      this.dateInput.nativeElement.value = this.currentValue;
    }
  }

  private syncDatepickerValue(value: Date | null): void {
    if (this.matDatepickerInput) {
      this.matDatepickerInput.value = value;
    }
  }

  private commitValue(value: Date | null): void {
    this.currentValue = value ? this.formatDate(value) : '';
    this.syncDisplayedValue();
    this.syncDatepickerValue(value);
  }

  private getInputValue(target: EventTarget | null): string {
    return (target as HTMLInputElement | null)?.value ?? '';
  }
}
